import { Fragment, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Page } from '../theme/elements/Page';
import { Box, Typography, useTheme, Stack } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { getError, setError } from 'state/reducers/benefits.reducer';
import { selectTranslations } from 'state/reducers/translation.reducers';
import { permissionEnums } from 'constants/permission.constants';
import { hasPermission } from 'components/AuthorizationComponent';
import { WarningDialog } from 'components/WarningDialog';
import { getPages } from 'state/reducers/content.reducers';
import _ from 'lodash';
import { getAllBenefits } from 'state/reducers/benefits.reducer';
import { getBenefits, createBenefit } from 'services/benefits.service';
import { IBenefit, benefitTypes } from 'constants/benefits.constants';
import { BenefitsTable } from 'sections/CustomerBenefitsManagement/BenefitsTable';
import FormCreatorComponent, { isValidForm } from 'components/FormCreatorComponent';
import { fieldMode, fieldsGroupType, fieldType } from 'components/FormCreatorComponent/types';
import { ButtonComponent } from 'components/ButtonComponent';
import { validationTypes } from 'components/FormCreatorComponent/types';
import { SmallCard } from 'theme/elements/Cards';
import { format } from 'date-fns';
import { pagesEnum, timing } from 'constants/ui.constants';
import { getToken } from 'state/reducers/auth.reducers';
import { getUserDetails } from 'state/reducers/user.reducers';

export const CustomerBenefitsManagement = () => {
	const theme = useTheme();
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const t = useAppSelector(selectTranslations);
	const { isLoading } = useAppSelector((state) => state.benefits);
	const pages = useAppSelector(getPages);
	const benefitsError = useAppSelector(getError);
	const token = useAppSelector(getToken);
	const { permissions } = useAppSelector(getUserDetails);
	const permissionList = Array.isArray(permissions) ? permissions : permissions?.permissions;
	const canView = hasPermission([permissionEnums.VIEW_BENEFIT]);
	const canCreate = hasPermission([permissionEnums.CREATE_BENEFIT]);
	const [formState, setFormState] = useState<Record<string, any>>({});
	const [formValues, setFormValues] = useState<Record<string, any>>({});

	const benefits = useAppSelector(getAllBenefits);

	const [benefitsToShow, setBenefitsToShow] = useState<any>(benefits);

	const addNewBenefitForm = {
		name: 'addNewBenefitForm',
		handleChange: () => {},
		fieldsGroups: [
			{
				type: fieldsGroupType.col,
				fields: [
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'name',
							label: 'page.benefits_management.forms.add.field.benefit_name',
							required: true,
							disabled: false,
							options: [],
						},
						validation: [{ requierd: true }],
					},
					{
						type: fieldType.dateRangePicker,
						mode: fieldMode.readonly,
						props: {
							name: 'expiry',
							label: 'page.benefits_management.forms.add.field.benefit_expiry',
							required: true,
							disabled: false,
							options: [],
							isInFuture: true,
						},
						validation: [{ requierd: true }],
					},
					{
						type: fieldType.select,
						mode: fieldMode.readonly,
						props: {
							name: 'type',
							label: 'page.benefits_management.forms.add.field.benefit_type',
							required: true,
							disabled: false,
							options: benefitTypes,
						},
						validation: [{ requierd: true }],
					},
					{
						type: fieldType.text,
						mode: fieldMode.readonly,
						props: {
							name: 'percentage',
							label: 'page.benefits_management.forms.add.field.benefit_value.placeholder',
							required: true,
							disabled: false,
						},
						validation: [
							{ requierd: true },
							{ [validationTypes.integer]: true },
							{ [validationTypes.maxNumber]: 100 },
							{ [validationTypes.minNumber]: 0.01 },
						],
					},
				],
			},
		],
	};

	useEffect(() => {
		if (token && (!permissionList || permissionList.length === 0)) {
			return;
		}
		if (!canView) {
			navigate(`/${pagesEnum.PAGE_NOT_FOUND}`);
		}
	}, [token, permissionList, canView, navigate]);

	useEffect(() => {
		if (canView) {
			dispatch(getBenefits());
		}
	}, [canView, dispatch]);

	const openErrorDialog = useMemo(() => {
		return benefitsError?.exist;
	}, [benefitsError]);

	const addNewBenefit = async () => {
		if (formState?.addNewBenefitForm?.isValid) {
			const benefitToCreate = {
				name: formState.addNewBenefitForm.dirtyFields.name,
				startDate: format(formState.addNewBenefitForm.dirtyFields.expiry.startDate, timing.DATE_FOR_SERVICE),
				expirationDate: format(formState.addNewBenefitForm.dirtyFields.expiry.endDate, timing.DATE_FOR_SERVICE),
				type: 'Convert',
				percentage: Number(formState.addNewBenefitForm.dirtyFields.percentage),
			};
			await dispatch(createBenefit(benefitToCreate));
			await dispatch(getBenefits());
			setFormState({});
			setFormValues({});
		}
	};

	if (!canView) {
		return null;
	}

	return (
		<Fragment>
			<WarningDialog
				open={openErrorDialog}
				subTitle={benefitsError?.text}
				handleCancel={() => {
					dispatch(setError({ exist: false }));
				}}
			/>
			<Page title={t('page.benefits_management.title')} loading={isLoading}>
				<>
					{canCreate && (
						<Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
							<SmallCard
								styleObj={{
									position: 'relative',
									width: '50%',
									padding: 4,
								}}
							>
								<Stack spacing={3}>
									<Typography
										variant="pageTitle"
										sx={{ color: '#12564F', fontWeight: '700', fontFamily: 'PloniBold' }}
									>
										{t('page.benefits_management.create_new_benefit')}
									</Typography>
									<FormCreatorComponent
										{...addNewBenefitForm}
										translation={t}
										values={formValues}
										checkInitValidation={false}
										handleChange={(newformState: Record<string, any>) => {
											setFormState({ ...formState, [addNewBenefitForm.name]: newformState });
										}}
										emptyFormWhenValuesEmpty={true}
									/>
								</Stack>
							</SmallCard>
							<ButtonComponent
								disabled={!formState?.addNewBenefitForm?.isValid}
								handleClick={() => addNewBenefit()}
								text={`${t('page.benefits_management.saveChanges')}`}
								icon={
									<img
										src={'/icons/save.svg'}
										style={{
											marginLeft: '10px',
											filter: formState?.addNewBenefitForm?.isValid
												? 'invert(100%) brightness(200%)'
												: 'invert(50%) brightness(100%)',
											width: '16px',
											height: '16px',
										}}
									/>
								}
								styleObj={{ flexDirection: 'row-reverse' }}
							/>
						</Box>
					)}
					<Stack gap={1}>
						<Typography variant="h4" sx={{ marginTop: '30px', fontWeight: '700', fontFamily: 'PloniBold' }}>
							{t('page.benefits_management.benefits_table')}
						</Typography>
						<BenefitsTable />
					</Stack>
				</>
			</Page>
		</Fragment>
	);
};
